import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { getMasterCategories, createMasterCategory, updateMasterCategory, deleteMasterCategory, seedMasterCategories, migrateMasterCategories, uploadMasterCategoryImage } from '../../services/api';
import { Layers, Plus, Edit, Trash2, X, Sparkles, RefreshCw, Upload, ImageIcon } from 'lucide-react';
import { API_URL } from '../../config';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const SOFTWARE_OPTIONS = [
  { value: '', label: 'Ninguno' },
  { value: 'enlocal_caja', label: 'enLocal Caja' },
  { value: 'comanda_pro', label: 'Comanda Pro' },
  { value: 'enlocal_servicios', label: 'enLocal Servicios' },
];

const MasterCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    software: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await getMasterCategories({ active_only: false });
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading master categories:', error);
      toast.error('Error al cargar categorias maestras');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      const response = await seedMasterCategories();
      toast.success(response.data.message);
      loadCategories();
    } catch (error) {
      console.error('Error seeding master categories:', error);
      toast.error('Error al crear categorias maestras');
    }
  };

  const handleMigrate = async () => {
    if (!window.confirm('Esto asignara categorias maestras a todos los productos existentes. Continuar?')) return;
    setMigrating(true);
    try {
      const response = await migrateMasterCategories();
      const c = response.data.counts;
      toast.success(`Migrados: ${c.restaurant} restaurante, ${c.retail} retail, ${c.category_fixed} categorias corregidas`);
    } catch (error) {
      console.error('Error migrating:', error);
      toast.error(getErrorMessage(error, 'Error en migracion'));
    } finally {
      setMigrating(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name) => {
    const newFormData = { ...formData, name };
    if (!editingItem) {
      newFormData.slug = generateSlug(name);
    }
    setFormData(newFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData, software: formData.software || null };

    try {
      if (editingItem) {
        await updateMasterCategory(editingItem.id, payload);
        toast.success('Categoria maestra actualizada');
      } else {
        await createMasterCategory(payload);
        toast.success('Categoria maestra creada');
      }
      setShowModal(false);
      resetForm();
      loadCategories();
    } catch (error) {
      console.error('Error saving master category:', error);
      toast.error(getErrorMessage(error, 'Error al guardar categoria maestra'));
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      slug: item.slug,
      description: item.description || '',
      icon: item.icon || '',
      software: item.software || '',
      order: item.order || 0,
      is_active: item.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Estas seguro de eliminar "${item.name}"?`)) return;

    try {
      await deleteMasterCategory(item.id);
      toast.success('Categoria maestra eliminada');
      loadCategories();
    } catch (error) {
      console.error('Error deleting master category:', error);
      toast.error(getErrorMessage(error, 'Error al eliminar categoria maestra'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      software: '',
      order: 0,
      is_active: true
    });
    setEditingItem(null);
  };

  const handleImageUpload = async (catId, file) => {
    try {
      await uploadMasterCategoryImage(catId, file);
      toast.success('Imagen actualizada');
      loadCategories();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir imagen');
    }
  };

  const getImageSrc = (item) => {
    const url = item.thumbnail_url || item.image_url;
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const getSoftwareLabel = (software) => {
    const opt = SOFTWARE_OPTIONS.find(o => o.value === (software || ''));
    return opt ? opt.label : software || 'Ninguno';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Categorias Maestras</h1>
          <p className="text-slate-500 mt-1">Categorias de primer nivel que agrupan por tipo de software/industria</p>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <p className="text-slate-600">{categories.length} categorias registradas</p>
          <div className="flex gap-3">
            {categories.length === 0 && (
              <button
                onClick={handleSeed}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Crear Categorias Iniciales
              </button>
            )}
            {categories.length > 0 && (
              <button
                onClick={handleMigrate}
                disabled={migrating}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${migrating ? 'animate-spin' : ''}`} />
                {migrating ? 'Migrando...' : 'Migrar Productos'}
              </button>
            )}
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nueva Categoria
            </button>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl border ${
                item.is_active ? 'border-slate-200' : 'border-red-200 bg-red-50'
              } p-4 hover:shadow-md transition-shadow`}
            >
              {/* Image + Upload */}
              <div className="flex items-center justify-center mb-3">
                <div className="relative group/img">
                  <div className="w-20 h-20 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden">
                    {getImageSrc(item) ? (
                      <img src={getImageSrc(item)} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-3xl">{item.icon || '📂'}</span>
                    )}
                  </div>
                  <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded-xl opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                    <Upload className="w-5 h-5 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files[0]) handleImageUpload(item.id, e.target.files[0]);
                      }}
                    />
                  </label>
                </div>
              </div>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{item.name}</h3>
                  <p className="text-xs text-slate-400 font-mono">{item.slug}</p>
                  {item.description && (
                    <p className="text-sm text-slate-500 line-clamp-1 mt-0.5">{item.description}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Orden: {item.order}</span>
                  {item.software && (
                    <span className="px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-600">
                      {getSoftwareLabel(item.software)}
                    </span>
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  item.is_active
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {item.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {categories.length === 0 && (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No hay categorias maestras</h3>
            <p className="text-slate-500 mt-1">Crea las categorias maestras para organizar productos por tipo de software</p>
            <button
              onClick={handleSeed}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Crear Categorias Iniciales
            </button>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingItem ? 'Editar Categoria' : 'Nueva Categoria Maestra'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Ej: Restaurantes"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Slug *</label>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="restaurantes"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Descripcion</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Ej: Restaurantes, cafeterias, fondas"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Software asociado</label>
                  <select
                    value={formData.software}
                    onChange={(e) => setFormData({ ...formData, software: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    {SOFTWARE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Icono (emoji)</label>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="🍽️"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Orden</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-slate-700">Categoria activa</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                  >
                    {editingItem ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MasterCategories;
